import { motion } from "framer-motion";
import { Helmet } from "react-helmet-async";
import { MessageCircle, Check } from "lucide-react";
import { WA_LINK, SERVICES } from "@/data";

const inView = {
  initial: { opacity: 0, y: 30 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-60px" },
};

function ServiceCard({ service, categoryLabel, gradient, isPremium = false }) {
  return (
    <motion.div
      className="relative rounded-3xl overflow-hidden bg-[#111111] border border-white/10 flex flex-col shadow-lg"
      style={{ background: gradient }}
      whileHover={{ scale: 1.03, y: -6 }}
      whileTap={{ scale: 0.97 }}
      transition={{ type: "spring", stiffness: 280, damping: 18 }}
      data-testid={`service-card-${service.name.replace(/\s+/g, "-").toLowerCase()}`}
    >
      <div className="relative h-44 overflow-hidden">
        <img
          src={service.image}
          alt={service.name}
          className="w-full h-full object-cover"
          loading="lazy"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#111111] via-black/20 to-transparent" />
      </div>
      <div className="p-5 flex flex-col flex-1">
        <span className="inline-block w-fit mb-3 px-3 py-1 rounded-full bg-white/10 backdrop-blur-sm border border-white/20 text-xs font-semibold text-white font-inter tracking-wide">
          {categoryLabel}
        </span>
        <h3 className="text-white text-lg font-bold leading-snug mb-1">{service.name}</h3>
        <p className="text-gray-400 text-sm font-inter mb-4 flex-1 leading-relaxed">{service.description}</p>
        <div className="flex items-center justify-between pt-3 border-t border-white/10 mt-auto">
          <span className="font-mono text-2xl font-black text-white">
            ₹{service.price.toLocaleString()}
          </span>
          {!isPremium && (
            <a
              href={WA_LINK}
              target="_blank"
              rel="noopener noreferrer"
              className="bg-white text-black text-sm font-semibold px-4 py-2 rounded-full hover:bg-gray-100 transition-colors min-h-[40px] flex items-center"
              data-testid="service-card-book-btn"
            >
              Book Now
            </a>
          )}
        </div>
      </div>
    </motion.div>
  );
}

const categories = [
  {
    key: "therapeutic",
    label: "Therapeutic Massages",
    pill: "Therapeutic",
    gradient: "linear-gradient(160deg, rgba(67,56,202,0.18) 0%, #111111 60%)",
    desc: "Evidence-based therapeutic techniques for pain relief, muscle recovery, and physical rehabilitation.",
  },
  {
    key: "relaxation",
    label: "Relaxation Massages",
    pill: "Relaxation",
    gradient: "linear-gradient(160deg, rgba(5,150,105,0.18) 0%, #111111 60%)",
    desc: "Indulgent relaxation therapies inspired by traditional healing traditions from around the world.",
  },
  {
    key: "premium",
    label: "Premium Wellness Therapies",
    pill: "Premium",
    gradient: "linear-gradient(160deg, rgba(147,51,234,0.20) 0%, #111111 60%)",
    desc: "Exclusive premium therapies crafted for the discerning gentleman seeking ultimate wellness.",
  },
];

export default function Services() {
  return (
    <>
      <Helmet>
        <title>Massage Services & Prices in Mumbai | Deep Tissue, Thai, Swedish | Raj Muscle Care</title>
        <meta name="description" content="Complete massage services menu with prices. Deep tissue (₹3,000), Swedish, Thai, Sports, Aromatherapy & premium therapies for men in Mumbai. Studio, home & hotel service in Andheri." />
        <meta name="keywords" content="massage services prices Mumbai, deep tissue massage price Mumbai, Thai massage Andheri, sports massage Mumbai, massage for men prices, premium massage Mumbai" />
        <link rel="canonical" href="https://rajmusclecarestudio.com/services" />
        <meta property="og:title" content="Massage Services & Prices in Mumbai | Raj Muscle Care Studio" />
        <meta property="og:description" content="13 massage services starting from ₹3,000. Deep tissue, Thai, Swedish, sports, aromatherapy & premium therapies for men. Andheri, Mumbai." />
        <meta property="og:url" content="https://rajmusclecarestudio.com/services" />
      </Helmet>
      <div className="min-h-screen bg-[#050505]">
      {/* Page Hero */}
      <div className="relative pt-32 pb-12 text-center overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-[#FF4500]/5 via-transparent to-transparent" />
        <div className="absolute top-20 left-1/2 -translate-x-1/2 w-80 h-80 bg-[#FF4500]/6 rounded-full blur-3xl" />
        <div className="relative z-10 max-w-3xl mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
          >
            <p className="text-xs tracking-[0.2em] uppercase font-semibold text-[#FF4500] mb-3 font-inter">
              Our Services
            </p>
            <h1 className="text-4xl sm:text-5xl text-white font-black mb-5 leading-tight">
              Massage Services
              <br />
              <span className="text-[#FF4500]">&amp; Pricing</span>
            </h1>
            <p className="text-gray-400 font-inter text-base max-w-xl mx-auto">
              Professional male massage therapy in Mumbai — starting from ₹3,000.
              Studio, Home &amp; Hotel Service available.
            </p>
          </motion.div>
        </div>
      </div>

      {/* Services Grid */}
      <section className="py-8 sm:py-12">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          {categories.map((cat) => (
            <div key={cat.key} className="mb-16">
              <motion.div {...inView} transition={{ duration: 0.5 }} className="mb-7">
                <h2 className="text-2xl sm:text-3xl text-white font-black font-playfair mb-2">
                  {cat.label}
                </h2>
                <p className="text-gray-400 text-sm font-inter">{cat.desc}</p>
              </motion.div>

              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                {SERVICES[cat.key].map((service, i) => (
                  <motion.div
                    key={service.name}
                    initial={{ opacity: 0, y: 24 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-40px" }}
                    transition={{ duration: 0.5, delay: i * 0.07 }}
                  >
                    <ServiceCard
                      service={service}
                      categoryLabel={cat.pill}
                      gradient={cat.gradient}
                      isPremium={cat.key === "premium"}
                    />
                  </motion.div>
                ))}
              </div>

              {cat.key === "premium" && (
                <motion.div
                  {...inView}
                  transition={{ duration: 0.6, delay: 0.2 }}
                  className="mt-6 p-6 rounded-2xl bg-[#111111] border border-white/10 text-center"
                >
                  <p className="text-gray-400 text-sm font-inter mb-4">
                    Customized premium therapies available on request. Contact for details.
                  </p>
                  <a
                    href={WA_LINK}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 bg-[#FF4500] hover:bg-[#e63d00] text-white font-semibold px-8 py-4 rounded-full transition-all hover:scale-105 min-h-[52px]"
                    data-testid="premium-enquire-btn"
                  >
                    <MessageCircle size={18} />
                    Enquire on WhatsApp
                  </a>
                </motion.div>
              )}
            </div>
          ))}
        </div>
      </section>

      {/* Bottom CTA */}
      <section className="py-14 bg-[#0a0a0a] border-t border-white/5">
        <div className="max-w-2xl mx-auto px-4 text-center">
          <motion.div {...inView} transition={{ duration: 0.6 }}>
            <h2 className="text-2xl sm:text-3xl text-white font-black mb-4">
              Ready to Book?
            </h2>
            <div className="flex flex-wrap justify-center gap-3 mb-7 text-sm font-inter text-gray-400">
              {["Same Day Service", "Home & Hotel Available", "100% Discreet"].map((item) => (
                <div key={item} className="flex items-center gap-1.5">
                  <Check size={13} className="text-[#FF4500]" />
                  {item}
                </div>
              ))}
            </div>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href={WA_LINK}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-center gap-2 bg-[#FF4500] hover:bg-[#e63d00] text-white font-semibold px-8 py-4 rounded-full transition-all hover:scale-105 min-h-[56px]"
                data-testid="services-book-btn"
              >
                <MessageCircle size={18} />
                Book on WhatsApp
              </a>
              <a
                href="tel:+919004250500"
                className="flex items-center justify-center gap-2 border border-white/30 text-white font-semibold px-8 py-4 rounded-full transition-all hover:bg-white/5 min-h-[56px]"
                data-testid="services-call-btn"
              >
                Call Now
              </a>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
    </>
  );
}
